import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import heroBackground from "../assets/images/hero-bg.jpg";
import perdenserImage from "../assets/images/perdenser2.jpg";
import proenderImage from "../assets/images/proender.jpg";
import angioCleanImage from "../assets/images/angio_clean/angio_clean1.jpg";
import hydraImage from "../assets/images/hydra/hydra1.jpg";
import neuroSoftImage from "../assets/images/neuro_soft/neuro_soft1.jpg";
import neuroCathSupportImage from "../assets/images/neuro_cath_support/neuro_cath_support1.jpg";
import neuroCathPlusImage from "../assets/images/neurocath_plus/neurocath_plus1.jpg";
import perfillerImage from "../assets/images/perfiller/perfiller41.png";
import placeholderImage from "../assets/images/perdenser.jpg";
import neurosheathCardImage from "../assets/images/neurosheath/neurosheath1.jpg";
import microcathetersCardImage from "../assets/images/microcatheters/mic1.jpg";

function Home() {
  const [activeCard, setActiveCard] = useState(null);
  const count1 = useMotionValue(0);
  const count2 = useMotionValue(0);
  const count3 = useMotionValue(0);

  const rounded1 = useTransform(count1, (latest) => Math.round(latest));
  const rounded2 = useTransform(count2, (latest) => Math.round(latest));
  const rounded3 = useTransform(count3, (latest) => Math.round(latest));

  React.useEffect(() => {
    const controls1 = animate(count1, 15, { duration: 2 });
    const controls2 = animate(count2, 1000, { duration: 2 });
    const controls3 = animate(count3, 50, { duration: 2 });

    return () => {
      controls1.stop();
      controls2.stop();
      controls3.stop();
    };
  }, []);

  const products = [
    {
      id: 1,
      title: "Proender®",
      description: "Single-Use Embolic Protection Device",
      details:
        "Specially designed protection system for carotid artery intervention",
      icon: "🔬",
    },
    {
      id: 2,
      title: "VascuGuard™",
      description: "Vascular Treatment System",
      details: "Innovative vascular protection and treatment technology",
      icon: "💉",
    },
    {
      id: 3,
      title: "NeuroCare",
      description: "Neurovascular Solutions",
      details: "Advanced neurovascular treatment systems",
      icon: "🧠",
    },
  ];

  // Order matches Products page navbar: perfiller, neurocath-plus, neuro-cath-support, microcatheter, neurosheath, neuro-soft, hydra, angio-clean
  const additionalProducts = [
    {
      id: 5,
      title: "Perfiller®",
      subtitle: "Expansible Embolic Coil System",
      description:
        "Expansible embolic coil system offering controlled expansion and improved packing density for aneurysm treatment.",
      img: perfillerImage,
      slug: "perfiller",
    },
    {
      id: 10,
      title: "NeuroCath Plus",
      subtitle: "Aspiration Catheter, Distal 30 cm Super Flexible Hybrit Catheter Design (BRAID + COIL)",
      description:
        "NeuroCath Plus exceptional trackability, and efficient aspiration with enhanced lumen and shaft design.",
      img: neuroCathPlusImage,
      slug: "neurocath-plus",
    },
    {
      id: 9,
      title: "NeuroCath Support",
      subtitle: "Distal Access Catheter",
      description:
        "NeuroCath Support Distal Access Catheter is designed for rapid access and enhanced support.",
      img: neuroCathSupportImage,
      slug: "neuro-cath-support",
    },
    {
      id: 11,
      title: "Microcatheters",
      subtitle: "NeuroCath Platform",
      description:
        "NeuroCath Microcatheters are designed to delivery of interventional devices provides enhanced access to the treatment site, improved stent trackability, and greater stent/flow diverter deployment accuracy.",
      img: microcathetersCardImage,
      slug: "microcatheter",
    },
    {
      id: 12,
      title: "NeuroSheath",
      subtitle: "Introducer Sheath System",
      description:
        "NeuroSheath guiding catheter dilator kit with smooth transitions, reliable radiopacity, and an easy valve adapter for fast, confident access.",
      img: neurosheathCardImage,
      slug: "neurosheath",
    },
    {
      id: 8,
      title: "NeuroSoft G.W",
      subtitle: "Soft Neurovascular G.W",
      description:
        "Exceptional trackability without compromising the support you need.",
      img: neuroSoftImage,
      slug: "neuro-soft",
    },
    {
      id: 6,
      title: "Hydra G.W",
      subtitle: "Hydrophilic G.W Family",
      description:
        "HYDRA offers excellent trackability and lubricity for smooth navigation through tortuous anatomy. Optimized for both diagnostic and interventional access.",
      img: hydraImage,
      slug: "hydra",
    },
    {
      id: 4,
      title: "Angio - Clean",
      subtitle: "Angiographic - Diagnostic Catheter",
      description:
        "Wide selection flush, cerebral, visceral and coronary shapes to meet individual needs.",
      img: angioCleanImage,
      slug: "angio-clean",
    },
  ];

  return (
    <div>
      <motion.header
        className="hero-section"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="hero-content">
          <motion.h1
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            Leading Medical Technology
          </motion.h1>
          <motion.p
            className="subtitle"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Innovative Solutions, Reliable Results
          </motion.p>
          <motion.div
            className="hero-buttons"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Link to="/products" className="primary-button">
              Our Products
            </Link>
            <Link to="/contact" className="secondary-button">
              Contact Us
            </Link>
          </motion.div>
        </div>
      </motion.header>

      <section className="stats-section">
        <div className="stats-container">
          <motion.div
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3>
              <motion.span>{rounded1}</motion.span>+
            </h3>
            <p>Years of Experience</p>
          </motion.div>
          <motion.div
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3>
              <motion.span>{rounded2}</motion.span>+
            </h3>
            <p>Case Support</p>
          </motion.div>
          <motion.div
            className="stat-item"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h3>
              <motion.span>{rounded3}</motion.span>+
            </h3>
            <p>Global Partners</p>
          </motion.div>
        </div>
      </section>

      <section className="product-family-section">
        <h2>Our Product Family</h2>
        <div className="product-cards">
          <div className="product-card">
            <motion.div
              className="product-card-inner"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
            <img
              src={proenderImage}
              alt="Proender Device"
              className="product-image"
            />
            <h3>Proender®</h3>
            <p className="product-subtitle">Embolic Protection Device</p>
            <p>
              Advanced protection device for carotid artery intervention,
              providing reliable protection against distal embolization with its
              innovative filter design.
            </p>
            <Link to="/products?product=proender" className="learn-more-btn">
              Learn More
            </Link>
            </motion.div>
          </div>

          <div className="product-card">
            <motion.div
              className="product-card-inner"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
            <img
              src={perdenserImage}
              alt="Perdenser Device"
              className="product-image"
            />
            <h3>Perdenser™</h3>
            <p className="product-subtitle">3D Perdenser Detachable Coil</p>
            <p>
              Offer excellent versatility, featuring a broad selection of sizes
              and varying degrees of softness to accommodate a wide range of
              clinical cases.
            </p>
            <Link to="/products?product=perrdenser" className="learn-more-btn">
              Learn More
            </Link>
            </motion.div>
          </div>

          {additionalProducts.map((p) => (
            <div key={p.id} className="product-card">
              <motion.div
                className="product-card-inner"
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img src={p.img || placeholderImage} alt={p.title} className="product-image" />
                <h3>{p.title}</h3>
                <p className="product-subtitle">{p.subtitle}</p>
                <p>
                  {p.description || `Learn about ${p.title} and its available configurations.`}
                </p>
                <Link to={`/products?product=${p.slug}`} className="learn-more-btn">
                  Learn More
                </Link>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <motion.div
          className="cta-content"
          whileInView={{ y: [50, 0], opacity: [0, 1] }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2>Future of Healthcare Technology</h2>
          <p>
            Contact us to learn more about our latest technologies and
            innovative solutions
          </p>
          <Link to="/contact" className="cta-button">
            Contact Us
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

export default Home;
